import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { PaginatedResponse } from '@app/core/models/shared/admin.model';
import { backendErrorText, downloadBlob, fileNameFromDisposition, toLocalDateTimePeriod } from '@app/core/utils';
import {
  MarketingPeriod,
  PromoCode,
  PromoCodePurchase,
  PromoCodePurchaseStatus,
  PromoCodeRefund,
  PromoCodeRequest,
  PartnerSalesReportFilters,
  PromoCodeSummary,
  ReferralClick,
  ReferralClickStatus,
  ReferralLink,
  ReferralPurchase,
  ReferralRefund,
  ReferralSummary,
  RewardSummary,
} from '@app/core/models/partner/partner-marketing.model';

function emptyPage<T>(): PaginatedResponse<T> {
  return {
    currentPageNumber: 0,
    pageSize: 0,
    totalElements: 0,
    totalPages: 0,
    isLastPage: true,
    isFirstPage: true,
    isEmpty: true,
    content: [],
  };
}

type QueryValue = string | number | null | undefined;

/**
 * Реферальная, промокодная статистика и вознаграждение партнера (админ).
 * Swagger: /api/b2b/admin/partners/{partnerId}/referral|promocodes|rewards
 */
@Injectable({
  providedIn: 'root',
})
export class PartnerMarketingService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  private partnerUrl(partnerId: string): string {
    return `${this.apiBaseUrl}/b2b/admin/partners/${partnerId}`;
  }

  private get promoCodesUrl(): string {
    return `${this.apiBaseUrl}/admin/discounts/promocodes`;
  }

  // ---------- Referral ----------

  /**
   * Ссылка выбранного партнера (GET — только чтение, ничего не создает).
   * В описании API для админа есть только PUT (он создает ссылку), поэтому любой 4xx на GET —
   * 404 «ссылка не найдена», 405 без GET и т.п. — считаем «ссылки нет»: без тоста, с кнопкой «Создать ссылку».
   * Тост — только для ошибок сервера и сети.
   */
  getReferralLink(partnerId: string): Observable<ReferralLink | null> {
    return this.http
      .get<ReferralLink>(`${this.partnerUrl(partnerId)}/referral/link`)
      .pipe(
        catchError(error =>
          error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500
            ? of(null)
            : this.fallback(error, 'Не удалось получить реферальную ссылку', null)
        )
      );
  }

  /** Получить существующую или создать постоянную реферальную ссылку партнера */
  createReferralLink(partnerId: string): Observable<ReferralLink | null> {
    return this.http
      .put<ReferralLink>(`${this.partnerUrl(partnerId)}/referral/link`, {})
      .pipe(catchError(error => this.fallback(error, 'Не удалось создать реферальную ссылку', null)));
  }

  getReferralSummary(partnerId: string, period: MarketingPeriod): Observable<ReferralSummary | null> {
    return this.http
      .get<ReferralSummary>(`${this.partnerUrl(partnerId)}/referral/summary`, {
        params: this.params({ ...period }),
      })
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить реферальную статистику', null)));
  }

  getReferralClicks(
    partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod,
    status?: ReferralClickStatus | null
  ): Observable<PaginatedResponse<ReferralClick>> {
    return this.http
      .get<PaginatedResponse<ReferralClick>>(`${this.partnerUrl(partnerId)}/referral/clicks`, {
        params: this.params({ ...period, status, page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить переходы по ссылке', emptyPage<ReferralClick>())
        )
      );
  }

  getReferralPurchases(
    partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod
  ): Observable<PaginatedResponse<ReferralPurchase>> {
    return this.http
      .get<PaginatedResponse<ReferralPurchase>>(`${this.partnerUrl(partnerId)}/referral/purchases`, {
        params: this.params({ ...period, page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить реферальные покупки', emptyPage<ReferralPurchase>())
        )
      );
  }

  getReferralPurchase(partnerId: string, purchaseId: string): Observable<ReferralPurchase | null> {
    return this.http
      .get<ReferralPurchase>(`${this.partnerUrl(partnerId)}/referral/purchases/${purchaseId}`)
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить покупку', null)));
  }

  getReferralRefunds(
    partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod
  ): Observable<PaginatedResponse<ReferralRefund>> {
    return this.http
      .get<PaginatedResponse<ReferralRefund>>(`${this.partnerUrl(partnerId)}/referral/refunds`, {
        params: this.params({ ...period, page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить возвраты', emptyPage<ReferralRefund>())
        )
      );
  }

  // ---------- Promo codes ----------

  getPromoCodes(partnerId: string, page: number, size: number): Observable<PaginatedResponse<PromoCode>> {
    return this.http
      .get<PaginatedResponse<PromoCode>>(`${this.partnerUrl(partnerId)}/promocodes`, {
        params: this.params({ page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить промокоды партнера', emptyPage<PromoCode>())
        )
      );
  }

  getPromoCodeSummary(
    partnerId: string,
    period: MarketingPeriod,
    code?: string | null
  ): Observable<PromoCodeSummary | null> {
    return this.http
      .get<PromoCodeSummary>(`${this.partnerUrl(partnerId)}/promocodes/summary`, {
        params: this.params({ ...toLocalDateTimePeriod(period), code }),
      })
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить статистику промокодов', null)));
  }

  getPromoCodePurchases(
    partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod,
    code?: string | null,
    status?: PromoCodePurchaseStatus | null
  ): Observable<PaginatedResponse<PromoCodePurchase>> {
    return this.http
      .get<PaginatedResponse<PromoCodePurchase>>(`${this.partnerUrl(partnerId)}/promocodes/purchases`, {
        params: this.params({ ...toLocalDateTimePeriod(period), code, status, page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить покупки по промокодам', emptyPage<PromoCodePurchase>())
        )
      );
  }

  getPromoCodeRefunds(
    partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod,
    code?: string | null
  ): Observable<PaginatedResponse<PromoCodeRefund>> {
    return this.http
      .get<PaginatedResponse<PromoCodeRefund>>(`${this.partnerUrl(partnerId)}/promocodes/refunds`, {
        params: this.params({ ...toLocalDateTimePeriod(period), code, page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить возвраты по промокодам', emptyPage<PromoCodeRefund>())
        )
      );
  }

  getPromoCode(promoId: string): Observable<PromoCode | null> {
    return this.http
      .get<PromoCode>(`${this.promoCodesUrl}/${promoId}`)
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить промокод', null)));
  }

  /** Создать партнерский промокод (source=PARTNER, partnerId — владелец) */
  createPromoCode(body: PromoCodeRequest): Observable<PromoCode> {
    return this.http
      .post<PromoCode>(this.promoCodesUrl, body)
      .pipe(catchError(error => this.rethrowPromoError(error, 'Не удалось создать промокод')));
  }

  /** Изменить скидку, срок действия, лимит или область применения промокода */
  updatePromoCode(promoId: string, body: PromoCodeRequest): Observable<PromoCode> {
    return this.http
      .put<PromoCode>(`${this.promoCodesUrl}/${promoId}`, body)
      .pipe(catchError(error => this.rethrowPromoError(error, 'Не удалось сохранить промокод')));
  }

  /** Отключить и удалить партнерский промокод */
  deletePromoCode(promoId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.promoCodesUrl}/${promoId}`)
      .pipe(catchError(error => this.rethrow(error, 'Не удалось удалить промокод')));
  }

  /** Восстановить ранее удаленный промокод */
  restorePromoCode(promoId: string): Observable<void> {
    return this.http
      .post<void>(`${this.promoCodesUrl}/${promoId}/restore`, {})
      .pipe(catchError(error => this.rethrow(error, 'Не удалось восстановить промокод')));
  }

  // ---------- Rewards ----------

  getRewardSummary(partnerId: string, period: MarketingPeriod): Observable<RewardSummary | null> {
    return this.http
      .get<RewardSummary>(`${this.partnerUrl(partnerId)}/rewards/summary`, {
        params: this.params({ ...period }),
      })
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить расчет вознаграждения', null)));
  }

  // ---------- Reports ----------

  /**
   * Excel-отчет по продажам партнера (листы «Сводка» и «Продажи») — скачивается файлом.
   * GET /api/b2b/admin/partners/{partnerId}/reports/sales.xlsx
   * Ошибку показывает тостом и пробрасывает дальше — диалог снимает спиннер и остается открытым.
   */
  downloadSalesReport(partnerId: string, filters: PartnerSalesReportFilters): Observable<void> {
    return this.http
      .get(`${this.partnerUrl(partnerId)}/reports/sales.xlsx`, {
        params: this.params({ ...filters, promoCode: filters.promoCode?.trim() }),
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(response => {
          const fileName =
            fileNameFromDisposition(response.headers.get('Content-Disposition')) ??
            `sales-report_${new Date().toISOString().slice(0, 10)}.xlsx`;
          downloadBlob(this.document, response.body ?? new Blob(), fileName);
        }),
        catchError(error =>
          from(this.parseBlobError(error)).pipe(
            switchMap(parsed => {
              const text = this.salesReportErrorText(parsed);
              console.error('Не удалось скачать отчет по продажам:', parsed);
              if (text.translated) {
                this.snackbarService.error(text.message);
              } else {
                this.translate.get(text.message).subscribe(msg => this.snackbarService.error(msg));
              }
              return throwError(() => parsed);
            })
          )
        )
      );
  }

  /** При responseType: 'blob' тело ошибки тоже приходит Blob'ом — превращаем его в JSON */
  private async parseBlobError(error: unknown): Promise<unknown> {
    if (!(error instanceof HttpErrorResponse) || !(error.error instanceof Blob)) return error;
    let body: unknown = null;
    try {
      body = JSON.parse(await error.error.text());
    } catch {
      // не JSON — остается без текста бэкенда
    }
    return new HttpErrorResponse({
      error: body,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText,
      url: error.url ?? undefined,
    });
  }

  private salesReportErrorText(error: unknown): { message: string; translated: boolean } {
    const status = error instanceof HttpErrorResponse ? error.status : 0;
    if (status === 413) return { message: 'partners.marketing.report.errors.tooLarge', translated: false };
    if (status === 403) return { message: 'partners.marketing.report.errors.forbidden', translated: false };
    if (status === 404) return { message: 'partners.marketing.report.errors.notFound', translated: false };
    const backend = backendErrorText(error);
    return backend
      ? { message: backend, translated: true }
      : { message: 'partners.marketing.report.errors.generic', translated: false };
  }

  // ---------- helpers ----------

  private params(values: Record<string, QueryValue>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(values)) {
      if (value === null || value === undefined || value === '') continue;
      params = params.set(key, String(value));
    }
    return params;
  }

  private notify(error: unknown, messageKey: string): void {
    console.error(`${messageKey}:`, error);
    this.translate.get(messageKey).subscribe(msg => this.snackbarService.error(msg));
  }

  private fallback<T>(error: unknown, messageKey: string, value: T): Observable<T> {
    this.notify(error, messageKey);
    return of(value);
  }

  /** 409 (занятый код) и ошибки полей показывает диалог промокода — под конкретными полями */
  private rethrowPromoError(error: unknown, messageKey: string): Observable<never> {
    const handledByForm =
      error instanceof HttpErrorResponse && (error.status === 409 || !!error.error?.fieldErrors);
    return handledByForm ? throwError(() => error) : this.rethrow(error, messageKey);
  }

  private rethrow(error: unknown, messageKey: string): Observable<never> {
    this.notify(error, messageKey);
    return throwError(() => error);
  }
}
